import type {
  CreateOrderRequest,
  Order,
  OrderStatus,
  PaginationInfo,
  UpdateOrderRequest,
} from "../types/api";
import { getAuthHeaders } from "../utils/auth";

/**
 * Create a new order
 */
export const createOrder = async (data: CreateOrderRequest): Promise<Order> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/orders`, {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const orderData: Order = await response.json();
    return orderData;
  } catch (error) {
    console.error("Error creating order:", error);
    throw error;
  }
};

/**
 * Get a single order by ID
 */
export const getOrder = async (orderId: string): Promise<Order> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/orders/${orderId}`, {
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const orderData: Order = await response.json();
    return orderData;
  } catch (error) {
    console.error("Error fetching order:", error);
    throw error;
  }
};

/**
 * Get all orders (admin only) with pagination and status filter support
 */
export const getAllOrders = async (
  page: number = 1,
  limit: number = 10,
  statusFilter?: OrderStatus[]
): Promise<{ orders: Order[]; pagination: PaginationInfo }> => {
  try {
    const headers = await getAuthHeaders();

    // Build query string with pagination and filters
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("limit", limit.toString());

    if (statusFilter && statusFilter.length > 0) {
      params.append("status", statusFilter.join(","));
    }

    const response = await fetch(`/api/orders?${params}`, {
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching all orders:", error);
    throw error;
  }
};

/**
 * Get all orders for a specific parent
 */
export const getOrdersByParent = async (parentId: string): Promise<Order[]> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/orders?parentId=${parentId}`, {
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const ordersData: Order[] = await response.json();
    return ordersData;
  } catch (error) {
    console.error("Error fetching orders by parent:", error);
    throw error;
  }
};

/**
 * Get all orders for a specific child
 */
export const getOrdersByChild = async (childId: string): Promise<Order[]> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/orders?childId=${childId}`, {
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const ordersData: Order[] = await response.json();
    return ordersData;
  } catch (error) {
    console.error("Error fetching orders by child:", error);
    throw error;
  }
};

/**
 * Update an order
 */
export const updateOrder = async (
  orderId: string,
  data: UpdateOrderRequest
): Promise<Order> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/orders/${orderId}`, {
      method: "PUT",
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const orderData: Order = await response.json();
    return orderData;
  } catch (error) {
    console.error("Error updating order:", error);
    throw error;
  }
};

/**
 * Update an order status
 */
export const updateOrderStatus = async (
  orderId: string,
  status: Order["status"]
): Promise<Order> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/orders/${orderId}/status`, {
      method: "PUT",
      headers,
      body: JSON.stringify({ status }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const orderData: Order = await response.json();
    return orderData;
  } catch (error) {
    console.error("Error updating order:", error);
    throw error;
  }
};

/**
 * Create Square payment for an order (public checkout endpoint)
 * Called when parent clicks "Proceed to Payment" on checkout page
 */
export const createOrderPayment = async (
  token: string,
  packageId: string,
  note?: string
): Promise<{ paymentLink: string; squareOrderId: string }> => {
  try {
    const response = await fetch(
      `/api/orders/public/${token}/square/create-payment`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ packageId, ...(note && { note }) }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      if (response.status === 400 && errorData.data) {
        // Return existing link info
        return errorData.data;
      }
      throw new Error(errorData.error || "Failed to create payment link");
    }

    const paymentData = await response.json();
    return paymentData;
  } catch (error) {
    console.error("Error creating order payment:", error);
    throw error;
  }
};
